/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: api.ts
 🗓️ Created: 2025-01-05
 🧩 Description:
    API configuration module for centralized API endpoint management,
    request headers, and authentication handling throughout the platform.

 🔧 Notes:
    - Centralizes all API endpoints and configuration
    - Implements multi-tenant support with tenant slug headers
    - Handles authentication token management
    - Provides helper functions for URL building and headers
    - Supports environment-based API URL configuration
    - Integrates with localStorage for token persistence

 🔁 Last Updated: 2025-01-05 by Rodrigo Cuadra
────────────────────────────────────────────────────────────────*/

// API Configuration
export const API_CONFIG = {
  BASE_URL: import.meta.env.VITE_API_URL || '',
  ENDPOINTS: {
    ROLE_PROFILES: '/api/role-profiles',
    LOG_PROFILES: '/api/log-profiles',
    USERS: '/api/users',
    AUTH: '/api/auth',
    MENU: '/api/profile-menu/menu-items',
    MODULES: '/api/modules',
    API_KEYS: '/api/api-keys',
    TENANTS: '/api/tenants',
    USER_TENANTS: '/api/users/options/tenants',
    PROFILE_MENU_ITEMS: '/api/profile-menu/menu-items',
    ROLE_PROFILES_MODULES: '/api/role-profiles/modules'
  }
};

// Debug logging disabled - application working correctly

// Helper function to build full API URLs
export const buildApiUrl = (endpoint: string): string => {
  return `${API_CONFIG.BASE_URL}${endpoint}`;
};

// Common headers for API requests
export const getApiHeaders = (tenantSlug: string = 'main', userId?: string | number | null) => {
  const headers: Record<string, string> = {
    'X-Tenant-Slug': tenantSlug,
    'X-User-Id': userId ? String(userId) : '',
    'Content-Type': 'application/json'
  };
  
  // NOTE: Admin uses HttpOnly cookies for auth — NO localStorage token here.
  // The Admin API reads the 'access_token' cookie automatically on every request.
  // Adding a Bearer header from localStorage would create a collision if the Billing
  // app ever writes to localStorage on the same domain.
  return headers;
};
